import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import './batches.css';
import { NewRunModal } from './NewRunModal';
import { BatchRunDrawer } from './BatchRunDrawer';
import { BatchesMainPanel } from './components/BatchesMainPanel';
import { BatchesSidebar } from './components/BatchesSidebar';
import { useBatches } from './hooks/useBatches';

const DESKTOP_SIDEBAR_QUERY = '(min-width: 1024px)';

function getInitialSidebarState() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true;
  }
  return window.matchMedia(DESKTOP_SIDEBAR_QUERY).matches;
}

export function BatchWorkspace() {
  const [searchParams] = useSearchParams();
  const [showRunModal, setShowRunModal] = useState(false);
  const [drawerProps, setDrawerProps] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(getInitialSidebarState);
  const batchUploadHandled = useRef(false);

  const queryBatchId = searchParams.get('batchId');
  const querySampleId = searchParams.get('sampleId');
  const queryPipeline = (searchParams.get('pipeline') || '').toLowerCase() || null;
  const queryMode = searchParams.get('mode');
  const parsedBatchId = queryBatchId ? Number(queryBatchId) : null;
  const parsedSampleId = querySampleId ? Number(querySampleId) : null;
  const queryBatchIdNumber = Number.isFinite(parsedBatchId) ? parsedBatchId : null;
  const querySampleIdNumber = Number.isFinite(parsedSampleId) ? parsedSampleId : null;

  const batchesState = useBatches(queryBatchIdNumber);

  const startNewRun = useCallback(() => {
    setShowRunModal(true);
  }, []);

  const handleRunModeSelect = (mode) => {
    setShowRunModal(false);
    setDrawerProps({ mode });
  };

  const handleDrawerClose = (refresh) => {
    setDrawerProps(null);
    if (refresh) {
      batchesState.loadBatches();
    }
  };

  const handleBatchCreated = (batch) => {
    batchesState.selectBatch(batch.id);
    handleDrawerClose(true);
  };

  const handleBatchSelect = (id) => {
    setDrawerProps(null);
    batchesState.selectBatch(id);
    if (
      typeof window.matchMedia === 'function' &&
      !window.matchMedia(DESKTOP_SIDEBAR_QUERY).matches
    ) {
      setIsSidebarOpen(false);
    }
  };

  useEffect(() => {
    if (queryMode === 'batch-upload' && !batchUploadHandled.current) {
      batchUploadHandled.current = true;
      startNewRun();
    }
  }, [queryMode, startNewRun]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const sidebarMediaQuery = window.matchMedia(DESKTOP_SIDEBAR_QUERY);
    const handleBreakpointChange = (event) => setIsSidebarOpen(event.matches);

    sidebarMediaQuery.addEventListener?.('change', handleBreakpointChange);
    return () => {
      sidebarMediaQuery.removeEventListener?.('change', handleBreakpointChange);
    };
  }, []);

  const showingInlineBatchForm =
    drawerProps?.mode?.startsWith('batch') && !drawerProps?.batchId;

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <div className="shrink-0 border-b border-gray-200 bg-white">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900">Samples &amp; Batches</div>
          <button
            className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            onClick={startNewRun}
          >
            New Run
          </button>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <aside
          id="batches-sidebar"
          className={`shrink-0 overflow-hidden border-r border-gray-200 bg-white transition-[width] duration-300 ${
            isSidebarOpen
              ? 'absolute inset-y-0 left-0 z-20 w-72 shadow-xl lg:static lg:z-auto lg:shadow-none'
              : 'hidden lg:block lg:w-16'
          }`}
        >
          <BatchesSidebar
            isOpen={isSidebarOpen}
            onToggle={() => setIsSidebarOpen((open) => !open)}
            projects={batchesState.projects}
            filters={batchesState.filters}
            pendingFilters={batchesState.pendingFilters}
            showVoid={batchesState.showVoid}
            loading={batchesState.loading}
            batches={batchesState.batches}
            visibleBatches={batchesState.visibleBatches}
            selectedBatchId={batchesState.selectedBatchId}
            onSearchChange={batchesState.updateSearch}
            onToggleVoid={batchesState.setShowVoid}
            onSelectBatch={handleBatchSelect}
            onQuickFinalize={batchesState.handleQuickFinalize}
            onStatusToggle={batchesState.togglePendingStatus}
            onProjectChange={(value) =>
              batchesState.updatePendingFilter('project', value)
            }
            onDateChange={(field, value) =>
              batchesState.updatePendingFilter(field, value)
            }
            onApplyFilters={batchesState.applyPendingFilters}
            onClearFilters={batchesState.clearFilters}
            onSyncPendingFilters={batchesState.syncPendingFilters}
          />
        </aside>
        {isSidebarOpen && (
          <button
            type="button"
            aria-label="Close batches sidebar"
            className="absolute inset-0 z-10 bg-slate-900/10 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto bg-white">
          <div className="sticky top-0 z-[1] border-b border-slate-200 bg-white px-2 py-2 lg:hidden">
            <button
              type="button"
              aria-controls="batches-sidebar"
              aria-expanded={isSidebarOpen}
              className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
              onClick={() => setIsSidebarOpen(true)}
            >
              <svg
                aria-hidden="true"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <path d="M4 5h12M4 10h12M4 15h12" strokeLinecap="round" />
              </svg>
              Batches
            </button>
          </div>
          <div className="p-2 sm:p-4">
            <BatchesMainPanel
              drawerProps={drawerProps}
              projects={batchesState.projects}
              selectedBatchId={batchesState.selectedBatchId}
              initialSampleId={querySampleIdNumber}
              initialPipeline={queryPipeline}
              onDrawerClose={handleDrawerClose}
              onBatchCreated={handleBatchCreated}
              onBatchFinalized={batchesState.loadBatches}
            />
          </div>
        </main>
      </div>

      {showRunModal && (
        <NewRunModal
          onClose={() => setShowRunModal(false)}
          onSelect={handleRunModeSelect}
        />
      )}

      {drawerProps?.mode?.startsWith('batch') && !showingInlineBatchForm && (
        <BatchRunDrawer
          open={true}
          batchId={drawerProps.batchId}
          onClose={handleDrawerClose}
          onCreated={(batch) => {
            batchesState.selectBatch(batch.id);
            handleDrawerClose(true);
          }}
          projects={batchesState.projects}
        />
      )}
    </div>
  );
}
